import React, { useEffect, useMemo, useState } from "react";
import { Box, Button, Flex, Heading, HStack, IconButton, Input, Modal, ModalBody, ModalCloseButton, ModalContent, ModalFooter, ModalHeader, ModalOverlay, Table, Tbody, Td, Th, Thead, Tr, useDisclosure, useToast, FormControl, FormLabel } from "@chakra-ui/react";
import { AddIcon, RepeatIcon, LinkIcon, EditIcon } from "@chakra-ui/icons";
import { Link as RouterLink } from "react-router-dom";
import rbacService from "../services/rbacService";
import type { User } from "../../rbac/types";

const UsersPage: React.FC = () => {
  const toast = useToast();
  const { isOpen, onOpen, onClose } = useDisclosure();

  const [loading, setLoading] = useState(false);
  const [items, setItems] = useState<User[]>([]);
  const [search, setSearch] = useState("");

  // form state
  const [editing, setEditing] = useState<User | null>(null);
  const [email, setEmail] = useState("");
  const [fullName, setFullName] = useState("");

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return items;
    return items.filter(u => u.email.toLowerCase().includes(q) || (u.full_name || "").toLowerCase().includes(q));
  }, [items, search]);

  const load = async () => {
    setLoading(true);
    try {
      const data = await rbacService.getUsers({ skip: 0, limit: 1000 });
      setItems(data);
    } catch (e: any) {
      toast({ status: "error", title: "Gagal memuat users", description: e?.response?.data?.detail || e?.message });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { void load(); }, []);

  const openCreate = () => {
    setEditing(null);
    setEmail("");
    setFullName("");
    onOpen();
  };

  const openEdit = (row: User) => {
    setEditing(row);
    setEmail(row.email);
    setFullName(row.full_name || "");
    onOpen();
  };

  const submit = async () => {
    try {
      if (editing) {
        // Update (only full_name can be changed)
        await rbacService.updateUser(editing.id, { full_name: fullName });
        toast({ status: "success", title: "User diperbarui" });
      } else {
        // Create
        if (!email.trim()) {
          toast({ status: "warning", title: "Email wajib diisi" });
          return;
        }
        await rbacService.createUser({ email, full_name: fullName || undefined });
        toast({ status: "success", title: "User dibuat" });
      }
      onClose();
      await load();
    } catch (e: any) {
      toast({ status: "error", title: "Operasi gagal", description: e?.response?.data?.detail || e?.message });
    }
  };

  return (
    <Box p={4}>
      <Flex justify="space-between" align="center" mb={4}>
        <Heading size="md">Users</Heading>
        <HStack>
          <IconButton aria-label="refresh" icon={<RepeatIcon />} onClick={() => load()} isLoading={loading} />
          <Button leftIcon={<AddIcon />} colorScheme="green" onClick={openCreate}>New User</Button>
        </HStack>
      </Flex>

      <HStack mb={3}>
        <Input placeholder="Cari user..." value={search} onChange={(e) => setSearch(e.target.value)} />
      </HStack>

      <Table size="sm" variant="striped">
        <Thead>
          <Tr>
            <Th>ID</Th>
            <Th>Email</Th>
            <Th>Nama</Th>
            <Th>Created</Th>
            <Th isNumeric>Actions</Th>
          </Tr>
        </Thead>
        <Tbody>
          {filtered.map((row) => (
            <Tr key={row.id}>
              <Td>{row.id}</Td>
              <Td>{row.email}</Td>
              <Td>{row.full_name}</Td>
              <Td>{row.created_at ? new Date(row.created_at).toLocaleString() : '-'}</Td>
              <Td isNumeric>
                <HStack justify="flex-end">
                  <IconButton aria-label="edit" icon={<EditIcon />} onClick={() => openEdit(row)} size="sm" />
                  <Button as={RouterLink} to={`/admin/users/${row.id}`} leftIcon={<LinkIcon />} size="sm" variant="outline">Roles</Button>
                  <Button as={RouterLink} to={`/admin/users/${row.id}/overrides`} leftIcon={<LinkIcon />} size="sm" variant="outline">Overrides</Button>
                </HStack>
              </Td>
            </Tr>
          ))}
        </Tbody>
      </Table>

      <Modal isOpen={isOpen} onClose={onClose} isCentered>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>{editing ? "Edit User" : "Create User"}</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <FormControl mb={3}>
              <FormLabel>Email</FormLabel>
              <Input 
                placeholder="user@example.com" 
                value={email} 
                onChange={(e) => setEmail(e.target.value)} 
                isDisabled={!!editing}
                type="email"
              />
            </FormControl>
            <FormControl>
              <FormLabel>Full Name (Optional)</FormLabel>
              <Input 
                placeholder="John Doe" 
                value={fullName} 
                onChange={(e) => setFullName(e.target.value)} 
              />
            </FormControl>
          </ModalBody>
          <ModalFooter>
            <Button mr={3} onClick={onClose}>Cancel</Button>
            <Button colorScheme="green" onClick={submit} isDisabled={!editing && !email.trim()}>Save</Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
};

export default UsersPage;
